import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import InventoryLayout from "@/components/inventory/InventoryLayout";
import Modal, {
  TextField,
  TextArea,
  PrimaryButton,
  SecondaryButton,
} from "@/components/inventory/Modal";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const emptyForm = { code: "", name: "", address: "" };

export default function WarehousesPage() {
  const { user } = useAuth();
  const canWrite = user && ["admin", "manager"].includes(user.role);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/warehouses", {
        params: { q: q || undefined },
      });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };
  const startEdit = (row) => {
    setEditing(row);
    setForm({ code: row.code, name: row.name, address: row.address || "" });
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editing) {
        await api.put(`/inventory/warehouses/${editing.id}`, {
          name: form.name.trim(),
          address: form.address || null,
        });
        toast.success("Warehouse updated.");
      } else {
        await api.post("/inventory/warehouses", {
          code: form.code.trim().toUpperCase(),
          name: form.name.trim(),
          address: form.address || null,
        });
        toast.success("Warehouse created.");
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`Deactivate warehouse "${row.code}"?`)) return;
    try {
      await api.delete(`/inventory/warehouses/${row.id}`);
      toast.success("Warehouse deactivated.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <InventoryLayout
      title="Warehouses"
      right={
        canWrite && (
          <PrimaryButton testid="wh-new-btn" onClick={startCreate}>
            + New warehouse
          </PrimaryButton>
        )
      }
    >
      <div className="mb-6 flex gap-3 max-w-md">
        <input
          data-testid="wh-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by code or name…"
          className="flex-1 h-11 px-3 border border-slate-300 bg-white text-sm focus:outline-none focus:border-[#002FA7] transition-colors"
        />
        <SecondaryButton testid="wh-search-btn" onClick={load}>Search</SecondaryButton>
      </div>

      <div className="border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Code</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Name</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Address</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Status</th>
              <th className="px-4 py-3 w-40" />
            </tr>
          </thead>
          <tbody data-testid="wh-tbody">
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500 text-sm">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500 text-sm">No warehouses yet.</td></tr>
            ) : (
              items.map((row) => (
                <tr key={row.id} className="border-b border-slate-100 last:border-b-0">
                  <td className="px-4 py-3 font-mono text-xs" data-testid={`wh-row-code-${row.id}`}>{row.code}</td>
                  <td className="px-4 py-3 font-medium">{row.name}</td>
                  <td className="px-4 py-3 text-slate-600">{row.address || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs uppercase tracking-[0.2em] font-semibold ${row.is_active ? "text-[#039855]" : "text-slate-400"}`}>
                      {row.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canWrite && (
                      <div className="flex gap-2 justify-end">
                        <button
                          data-testid={`wh-edit-${row.id}`}
                          onClick={() => startEdit(row)}
                          className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700 hover:text-[#002FA7]"
                        >
                          Edit
                        </button>
                        {row.is_active && (
                          <button
                            data-testid={`wh-delete-${row.id}`}
                            onClick={() => onDelete(row)}
                            className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D92D20] hover:opacity-80"
                          >
                            Deactivate
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        testid="wh"
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? "Edit warehouse" : "New warehouse"}
      >
        <form onSubmit={onSubmit} className="space-y-5">
          <TextField
            label="Code"
            testid="wh-form-code"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            required
            disabled={!!editing}
            hint="Uppercase alphanumeric, hyphen/underscore allowed (e.g., WH-MAIN)."
          />
          <TextField
            label="Name"
            testid="wh-form-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
          <TextArea
            label="Address"
            testid="wh-form-address"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
          />
          <div className="flex justify-end gap-3 pt-2">
            <SecondaryButton testid="wh-form-cancel" type="button" onClick={() => setOpen(false)}>Cancel</SecondaryButton>
            <PrimaryButton testid="wh-form-submit" type="submit" disabled={submitting}>
              {submitting ? "Saving…" : editing ? "Save changes" : "Create"}
            </PrimaryButton>
          </div>
        </form>
      </Modal>
    </InventoryLayout>
  );
}
